<script lang="ts">
	import { onMount } from 'svelte';
	import { EditionsMixpanelEvents } from '../../constants/mixpanel-events';
	import { EditionsRoutes } from '../../constants/routes';
	import { EditionsIcon } from './EditionsIcon';
	import type { DropTypes } from '#lib/features/checkout/utils/createCartIconContext/createCartIconContext.js';
	import {
		ALL,
		getCartIconContext,
	} from '#lib/features/checkout/utils/getCartIconContext/index.js';
	import { getCheckoutContext } from '#lib/features/checkout/utils/getCheckoutContext/index.js';
	import { getLiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/utils/getLiveDropInfoContext/getLiveDropInfoContext.js';
	import { CartNavBar } from '#lib/features/navigation/components/CartNavBar/index.js';
	import { GlobalSearch } from '#lib/features/search/global/components/GlobalSearch/index.js';
	import { getTimeContext } from '#lib/features/time/utils/getTimeContext/getTimeContext.js';
	import { page } from '$app/state';
	import { SearchPageContainer } from '$global/features/search/components/SearchPageContainer';
	import { createSearchCombobox } from '$global/features/search/utils/createSearchCombobox';
	import { getDirectusDateTimestamp } from '$global/utils/getDirectusDateTimestamp';

	interface Props {
		children?: import('svelte').Snippet;
	}

	let { children }: Props = $props();

	const siteLinks = [
		{
			label: 'Editions',
			mobileLabel: 'All editions',
			href: EditionsRoutes.Gallery,
			dataCy: 'editions',
		},
		{
			label: 'Artists',
			mobileLabel: 'Edition artists',
			href: '/artists?with=editions#artists-list',
			dataCy: 'artists',
		},
	];

	const combobox = createSearchCombobox();

	const {
		cartItems: { clearCart, cartItems, removeFromCart },
		checkoutForm: { clearForm, form },
		orderConfirmation: { clearOrderConfirmation },
	} = getCheckoutContext();

	const { enableCartIcon, hideCartIcon, setDropTypes } = getCartIconContext();

	const { fetchTime, interval } = getTimeContext();
	let { liveDropInfoCtx } = getLiveDropInfoContext();

	onMount(() => {
		if (!$form[ALL]) {
			clearForm();
		}

		// Clears the UTC time interval when the user leaves HENI Editions
		return () => {
			if ($interval) {
				clearInterval($interval);
			}

			interval.set(null);
		};
	});

	$effect(() => {
		if (
			liveDropInfoCtx.liveDropInfo?.liveEditionsCodes &&
			(page.url.pathname || page.url.search)
		) {
			// Fetches current UTC time, stores it globally time and set an interval that will update it every second
			fetchTime().then((currentTime) => {
				// Stores the last drop type (purchase or application)
				setDropTypes(
					Object.values(liveDropInfoCtx.liveDropInfo?.editions || {})?.reduce(
						(accumulator: DropTypes, edition) => ({
							...accumulator,
							[edition.productId as string]: {
								type: edition?.type as string,
								dropPageSlug: edition?.slug as string,
								hybridConfig: edition?.hybridConfig ?? null,
								setDiscounts: edition?.setDiscounts ?? [],
							},
						}),
						{}
					)
				);

				// If the current time has not been returned
				// We don't enable the checkout and clear the cart + checkout form
				if (!currentTime) {
					hideCartIcon();
					clearCart();
					clearForm();
					clearOrderConfirmation();
				} else {
					// If the cart_end_date is in the future
					// We store the code of every purchasable edition

					const showCartIcon = Object.values(
						liveDropInfoCtx.liveDropInfo?.editions || {}
					).some(
						(edition) =>
							currentTime < getDirectusDateTimestamp(edition?.checkoutEndDate)
					);

					if (showCartIcon) {
						enableCartIcon();

						const editionsToRemove = Object.values(
							liveDropInfoCtx.liveDropInfo?.editions || {}
						)
							?.filter(
								(edition) =>
									!(
										liveDropInfoCtx.liveDropInfo?.liveEditionsCodes ?? []
									).includes(edition?.code as string)
							)
							?.filter(
								(edition) =>
									!!edition.productId && !!$cartItems[edition.productId]
							);

						const dropSlugsToRemove = [
							...new Set(
								editionsToRemove?.map(
									(editionToRemove) => editionToRemove?.slug
								)
							),
						];

						editionsToRemove?.forEach((editionToRemove) => {
							removeFromCart(editionToRemove.productId, EditionsMixpanelEvents);
						});

						dropSlugsToRemove.forEach((dropPageSlug) => {
							if (dropPageSlug) {
								clearForm(dropPageSlug);
								clearOrderConfirmation(dropPageSlug);
							}
						});
					} else {
						hideCartIcon();
						clearCart();
						clearForm();
						clearOrderConfirmation();
					}
				}
			});
		}
	});
</script>

<CartNavBar
	SearchComponent={GlobalSearch}
	{combobox}
	intermittentCart
	{siteLinks}
	isSearchEnabled={true}
>
	{#snippet homeIcon()}
		<EditionsIcon />
	{/snippet}
</CartNavBar>

<SearchPageContainer {combobox}>
	{@render children?.()}
</SearchPageContainer>
